import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Megaphone, Calendar, Briefcase, Bell, Trash2 } from "lucide-react";

const CAT = {
  General: { color: "#0F2E4A", icon: Megaphone, label: "General" },
  Holiday: { color: "#D97706", icon: Calendar, label: "Holiday" },
  Exam: { color: "#DC2626", icon: Bell, label: "Exam" },
  Placement: { color: "#059669", icon: Briefcase, label: "Placement" },
};

export default function Announcements() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", category: "General" });

  const load = async () => {
    const { data } = await api.get("/announcements"); setItems(data);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/announcements", form);
      toast.success("Announcement posted");
      setOpen(false); setForm({ title: "", body: "", category: "General" });
      load();
    } catch (err) { toast.error(formatErr(err?.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete announcement?")) return;
    await api.delete(`/announcements/${id}`); load();
  };

  const filtered = filter === "all" ? items : items.filter((i) => i.category === filter);

  return (
    <div className="space-y-6" data-testid="announcements-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#64748B] mb-1">Notice Board</div>
          <h1 className="font-heading text-3xl font-bold text-[#0F172A]">Announcements</h1>
        </div>
        {user.role !== "student" && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-announcement-button" className="bg-[#0F2E4A] hover:bg-[#174169] text-white">
                <Plus className="w-4 h-4 mr-1.5" /> New Announcement
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Create Announcement</DialogTitle></DialogHeader>
              <form onSubmit={create} className="space-y-4">
                <div>
                  <Label className="text-xs font-semibold text-[#475569]">Title *</Label>
                  <Input data-testid="ann-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required className="mt-1.5" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-[#475569]">Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="ann-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="General">General</SelectItem>
                      <SelectItem value="Holiday">Holiday</SelectItem>
                      <SelectItem value="Exam">Exam Schedule</SelectItem>
                      <SelectItem value="Placement">Placement</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-[#475569]">Body *</Label>
                  <Textarea data-testid="ann-body" rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} required className="mt-1.5" />
                </div>
                <DialogFooter>
                  <Button type="submit" data-testid="save-announcement" className="bg-[#0F2E4A] hover:bg-[#174169] text-white">Post</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {["all", "General", "Holiday", "Exam", "Placement"].map((k) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            data-testid={`filter-${k}`}
            className={`px-3 py-1.5 rounded-md text-xs font-medium uppercase tracking-wider transition-colors ${
              filter === k ? "bg-[#0F2E4A] text-white" : "bg-white border border-[#E2E8F0] text-[#475569] hover:bg-[#F8FAFC]"
            }`}
          >
            {k === "all" ? "All" : CAT[k]?.label}
          </button>
        ))}
      </div>

      <div className="space-y-3 stagger">
        {filtered.length === 0 && <div className="text-sm text-[#64748B] py-12 text-center">No announcements.</div>}
        {filtered.map((a) => {
          const meta = CAT[a.category] || CAT.General;
          const Icon = meta.icon;
          return (
            <div key={a.id} className="bg-white border border-[#E2E8F0] rounded-lg p-5 hover:shadow-md transition-all">
              <div className="flex gap-4">
                <div className="w-10 h-10 shrink-0 rounded-md grid place-items-center" style={{ backgroundColor: `${meta.color}15` }}>
                  <Icon className="w-5 h-5" style={{ color: meta.color }} strokeWidth={1.75} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded" style={{ backgroundColor: `${meta.color}10`, color: meta.color }}>{meta.label}</span>
                    <span className="text-[11px] text-[#64748B]">{new Date(a.created_at).toLocaleString()}</span>
                    <span className="text-[11px] text-[#64748B]">· by {a.created_by}</span>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-[#0F172A] mt-1.5">{a.title}</h3>
                  <p className="text-sm text-[#475569] mt-1 whitespace-pre-wrap">{a.body}</p>
                </div>
                {user.role !== "student" && (
                  <button onClick={() => del(a.id)} data-testid={`del-ann-${a.id}`} className="p-2 hover:bg-[#FEF2F2] rounded h-fit">
                    <Trash2 className="w-4 h-4 text-[#DC2626]" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
